import { useCallback, useMemo, useState } from 'react';
import type { IDataTableSource } from '../dataTableServerSource';
import { dxloSource, IDxloSourceOptions } from './dxloSource';

export interface IUseDxloSourceOptions<T, R = T> extends IDxloSourceOptions<T, R> {
    /**
     * Параметры набора (тип записей, папка, период — что уходит в запрос помимо loadOptions таблицы). Источник
     * создаётся заново, только когда они меняются по значению (JSON): новый `load` — другая выборка, таблица
     * начинает её с первой порции и снимает выделение. Остальные опции берутся с рендера, где сменились
     * параметры, — всё, от чего зависит выборка, должно быть здесь.
     */
    params?: unknown;
    /** Перечитать тот же набор по токену страницы (проп компонента) — вместе с `refresh()`. */
    refreshToken?: number;
}

/** Источник таблицы и перечитывание того же набора с сохранением места («Обновить», после сохранения). */
export interface IDxloSource<R> extends IDataTableSource<R> {
    refresh: () => void;
}

/**
 * Источник DataTable над dxlo-эндпоинтом (см. `dxloSource`), стабильный по параметрам набора: `fetch` и другие
 * функции опций пишут прямо в вызове. Без `keyOf` «выбрать все» по всем страницам — явный
 * `selectAllMode="allPages"`: ключи таблица берёт у `load` всем набором (`take=0` — DataSourceLoader отдаёт выборку
 * без страниц).
 */
export function useDxloSource<T, R = T>({
    params,
    refreshToken: externalToken = 0,
    ...options
}: IUseDxloSourceOptions<T, R>): IDxloSource<R> {
    const paramsKey = JSON.stringify(params ?? null);
    // набор задают params: функции опций новые на каждом рендере, а load обязан быть стабильным
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const source = useMemo(() => dxloSource(options), [paramsKey]);
    const [ownToken, setOwnToken] = useState(0);
    const refresh = useCallback(() => setOwnToken((token) => token + 1), []);
    return useMemo(
        () => ({ ...source, refreshToken: ownToken + externalToken, refresh }),
        [source, ownToken, externalToken, refresh],
    );
}
